import React from "react";
import { useQuery } from "@apollo/client";
import { KeyRound, X } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "~/components/ui/button";
import { useAuth } from "@components/auth-provider/AuthProvider";
import { useCurrentUser } from "@modules/authentication/useCurrentUser";
import { Routes } from "~/routes";
import {
  PasswordAuthStatusDocument,
  PasswordAuthStatusQuery,
} from "generated/graphql";

const getDismissedKey = (userId: string) =>
  `password-setup-banner-dismissed:${userId}`;

export const PasswordSetupBanner = () => {
  const auth = useAuth();
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.id;
  const [dismissed, setDismissed] = React.useState(true);

  const { data, loading } = useQuery<PasswordAuthStatusQuery>(
    PasswordAuthStatusDocument,
    {
      skip: !auth.isAuthenticated || !userId,
      fetchPolicy: "network-only",
    }
  );

  React.useEffect(() => {
    if (!userId) {
      setDismissed(true);
      return;
    }

    setDismissed(localStorage.getItem(getDismissedKey(userId)) === "true");
  }, [userId]);

  if (
    !auth.isAuthenticated ||
    !userId ||
    loading ||
    dismissed ||
    data?.passwordAuthStatus.hasPassword !== false
  ) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(getDismissedKey(userId), "true");
    setDismissed(true);
  };

  return (
    <div className="relative flex flex-col gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-start gap-3 pr-8 sm:pr-0">
        <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <KeyRound className="size-4" />
        </div>
        <div className="min-w-0 space-y-1">
          <p className="font-medium text-foreground">Set a password</p>
          <p className="max-w-2xl text-muted-foreground">
            You can set a password from profile settings if you would rather
            sign in without waiting for a magic link.
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 pl-12 sm:pl-0">
        <Button asChild variant="outline" size="sm">
          <Link to={Routes.ProfileSettings}>Go to profile settings</Link>
        </Button>
      </div>
      <button
        type="button"
        className="absolute right-3 top-3 rounded-sm p-1 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-foreground"
        onClick={handleDismiss}
        aria-label="Dismiss set password reminder"
      >
        <X className="size-4" />
      </button>
    </div>
  );
};
